import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Box from '@material-ui/core/Box';
import SvgIcon from '@material-ui/core/SvgIcon';
import IconButton from '@material-ui/core/IconButton';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import Hidden from '@material-ui/core/Hidden';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popper from '@afosto/components-react/Popper/Popper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ListItemText from '@material-ui/core/ListItemText';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Title from '@afosto/components-react/Title';
import Icon from '../Icon';

const DataGridPreviewHeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const DataGridPreviewHeaderTitle = styled.div`
  flex: 1;
  padding-top: ${props => props.theme.spacing(3)}px;
  padding-bottom: ${props => props.theme.spacing(3)}px;
`;

const DataGridPreviewHeader = ({ title, menu, onClose }) => {
  const intl = useIntl();
  const [previewActionsIsOpen, setPreviewActionsIsOpen] = useState(false);
  const previewActionsButtonRef = useRef(null);

  /**
   * Close the action menu.
   * @param {Event} event
   */
  const handleClosePreviewActions = event => {
    if (previewActionsButtonRef.current.contains(event.target)) {
      return;
    }

    setPreviewActionsIsOpen(false);
  };

  const shortcuts = [...menu]
    .filter(menuItem => menuItem.shortcut)
    .map(({ key, icon, onClick, translationKey, label }) => {
      const menuItemLabel = translationKey
        ? intl.formatMessage({
            id: translationKey,
            defaultMessage: label,
            description: `Label for preview menu item: ${label}`,
          })
        : label;

      const handleOnClick = event => {
        onClick(event);
        setPreviewActionsIsOpen(false);
      };

      return (
        <IconButton onClick={handleOnClick} key={`${key}_shortcut`} title={menuItemLabel}>
          <Icon icon={['fal', icon]} size="small" />
        </IconButton>
      );
    });

  return (
    <DataGridPreviewHeaderContainer>
      <Hidden lgUp implementation="js">
        <IconButton
          onClick={onClose}
          style={{ position: 'absolute', color: '#fff', top: '24px', left: '-50px' }}
        >
          <Icon icon={['fal', 'times-circle']} />
        </IconButton>
      </Hidden>
      <Hidden mdDown implementation="js">
        <IconButton onClick={onClose} style={{ marginTop: -4 }}>
          <Icon icon={['fal', 'times-circle']} />
        </IconButton>
      </Hidden>
      <DataGridPreviewHeaderTitle>
        {typeof title === 'string' ? <Title title={title} /> : title}
      </DataGridPreviewHeaderTitle>
      {menu.length > 0 && (
        <Box ml="auto">
          {shortcuts}
          <IconButton
            onClick={() => setPreviewActionsIsOpen(!previewActionsIsOpen)}
            buttonRef={previewActionsButtonRef}
          >
            <SvgIcon>
              <FontAwesomeIcon fixedWidth icon={['fal', 'ellipsis-v']} />
            </SvgIcon>
          </IconButton>
          <Popper
            handleClose={handleClosePreviewActions}
            anchorEl={previewActionsButtonRef}
            open={previewActionsIsOpen}
            position="bottom-end"
          >
            <ClickAwayListener onClickAway={handleClosePreviewActions}>
              <MenuList>
                {menu.map(({ key, translationKey, label, icon, color, onClick }) => (
                  <MenuItem
                    dense
                    key={key}
                    onClick={event => {
                      onClick(event);
                      setPreviewActionsIsOpen(false);
                    }}
                  >
                    {icon && (
                      <ListItemIcon>
                        <Icon icon={['fal', icon]} size="small" color={color} />
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={
                        translationKey
                          ? intl.formatMessage({
                              id: translationKey,
                              defaultMessage: label,
                              description: `Label for preview menu item: ${label}`,
                            })
                          : label
                      }
                      primaryTypographyProps={{ color: color || 'initial' }}
                    />
                  </MenuItem>
                ))}
              </MenuList>
            </ClickAwayListener>
          </Popper>
        </Box>
      )}
    </DataGridPreviewHeaderContainer>
  );
};

DataGridPreviewHeader.propTypes = {
  title: PropTypes.oneOfType([
    PropTypes.element,
    // eslint-disable-next-line react/no-typos
    PropTypes.elementType,
    PropTypes.node,
    PropTypes.string,
  ]),
  menu: PropTypes.array,
  onClose: PropTypes.func,
};

DataGridPreviewHeader.defaultProps = {
  title: undefined,
  menu: [],
  onClose: undefined,
};

export default DataGridPreviewHeader;
